import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ActorRefFrom } from 'xstate';
import { LoadingSpinner, Table, TableActions, TableCard, Wizard } from '~/components';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import { useJobListingRef } from '../../marketSelectors';
import { ListingFeeDialog } from '../ListingFeeDialog';
import { jobListingMachine } from './jobListing.machine';
import { JobListingRefContext } from './jobListingProvider';
import { TABLE_COLUMNS } from './tableCols';
import { createWizardSteps } from './wizardSteps';

const TAGS = CT.employer.jobListings;

export const JobListingDetails = () => {
  const { loading, jobListingRef } = useJobListingRef();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!jobListingRef) return null;

  return <JobListingDetailsContent jobListingRef={jobListingRef} loading={loading} />;
};

export const JobListingDetailsContent = ({
  jobListingRef,
  loading: marketLoading,
}: {
  jobListingRef: ActorRefFrom<typeof jobListingMachine>;
  loading: boolean;
}) => {
  const listingLoading = useSelector(jobListingRef, (state) => state?.context?.loading ?? false);
  const modal = useSelector(jobListingRef, (state) => state?.context?.modal);
  const currentStep = useSelector(jobListingRef, (state) => state?.context?.currentStep);
  const listings = useSelector(jobListingRef, (state) => state?.context?.listings);
  const detail = useSelector(jobListingRef, (state) => state?.context?.listing?.detail);
  // [MCV-1933] Set while the employer is asked to confirm the listing fee before publishing.
  const feePreview = useSelector(jobListingRef, (state) =>
    state?.matches?.('$_FEE_CONFIRM') ? state.context.feePreview : undefined
  );
  const loading = marketLoading || listingLoading;

  if (!modal || currentStep == null || !listings) return null;

  const openModal = () => {
    jobListingRef.send({ type: 'ON_OPEN_MODAL' });
  };

  const closeModal = () => {
    jobListingRef.send({ type: 'ON_CLOSE_MODAL' });
  };

  const handleDelete = (id: number) => {
    jobListingRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };

  const handleEdit = (id: number) => {
    jobListingRef.send({ type: 'ON_EDIT', payload: { id } });
  };

  return (
    <JobListingRefContext.Provider value={jobListingRef}>
      <TableCard
        title="Job Listings"
        addLabel="Create New Listing"
        onAddClick={openModal}
        addBtnTag={TAGS.table.createBtn}
        addAccessory={<TutorialVideoButton videoId="jobListing" tooltipAlign="start-then-end" />}
        layoutTag={TAGS.table.layout}
        disabledBtnTooltip={loading}
        disabledTitle="Job listings are loading"
      >
        <Table
          actions={[TableActions.edit, TableActions.delete]}
          tags={TAGS.table}
          rowData={listings}
          columns={TABLE_COLUMNS}
          onEdit={handleEdit}
          onDelete={handleDelete}
          loading={loading}
        />
      </TableCard>
      <ModalDialog open={modal.create || modal.edit} onClose={closeModal} xButton>
        <Wizard
          steps={createWizardSteps(jobListingRef, detail, loading)}
          currentStep={currentStep}
        />
      </ModalDialog>
      <ListingFeeDialog
        preview={feePreview ?? null}
        onConfirm={() => jobListingRef.send({ type: 'ON_CONFIRM_FEE' })}
        onCancel={() => jobListingRef.send({ type: 'ON_CANCEL_FEE' })}
      />
    </JobListingRefContext.Provider>
  );
};
